import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect } from "react";
import LoadingButton from "../../../components/LoadingButton";
import axios from "axios";
import { hostname, parseError } from "../../../utils";

interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
  companyId: string;
}

interface ManageTagsDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
}

export default function ManageTagsDialog({
  open,
  onClose,
  companyId,
  getToken,
  eventListener,
}: ManageTagsDialogProps) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [newTagName, setNewTagName] = useState("");
  const [newTagDescription, setNewTagDescription] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [deleteConfirmTagId, setDeleteConfirmTagId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open, companyId]);

  const loadTags = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/tags`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId },
      });
      setTags(response.data);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async () => {
    if (!newTagName.trim()) return;

    setLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/tags`,
        {
          companyId,
          name: newTagName.trim(),
          description: newTagDescription.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewTagName("");
      setNewTagDescription("");
      setIsAddingNew(false);
      await loadTags();
      eventListener({
        type: "SET_SUCCESS",
        message: "Tag created successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !newTagName.trim()) return;

    setLoading(true);
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/tags/${editingTag.id}`,
        {
          name: newTagName.trim(),
          description: newTagDescription.trim() || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEditingTag(null);
      setNewTagName("");
      setNewTagDescription("");
      await loadTags();
      eventListener({
        type: "SET_SUCCESS",
        message: "Tag updated successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    setLoading(true);
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/tags/${tagId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadTags();
      eventListener({
        type: "SET_SUCCESS",
        message: "Tag deleted successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
      setDeleteConfirmTagId(null);
    }
  };

  const startEdit = (tag: Tag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setNewTagDescription(tag.description || "");
    setIsAddingNew(false);
  };

  const cancelEdit = () => {
    setEditingTag(null);
    setNewTagName("");
    setNewTagDescription("");
    setIsAddingNew(false);
  };

  const handleClose = () => {
    cancelEdit();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Tags
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Create and manage tags to organize your knowledge base documents.
          Tags can be applied to documents to categorize and filter them.
        </Typography>

        {/* Add/Edit Tag Form */}
        {(isAddingNew || editingTag) && (
          <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editingTag ? "Edit Tag" : "Add New Tag"}
            </Typography>
            <TextField
              fullWidth
              label="Tag Name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              autoFocus
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (Optional)"
              value={newTagDescription}
              onChange={(e) => setNewTagDescription(e.target.value)}
              disabled={loading}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <LoadingButton
                variant="contained"
                onClick={editingTag ? handleUpdateTag : handleAddTag}
                loading={loading}
                disabled={!newTagName.trim() || loading}
              >
                {editingTag ? "Update" : "Add"}
              </LoadingButton>
              <Button onClick={cancelEdit} disabled={loading}>
                Cancel
              </Button>
            </Box>
          </Paper>
        )}

        {/* Add New Tag Button */}
        {!isAddingNew && !editingTag && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setIsAddingNew(true)}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            Add New Tag
          </Button>
        )}

        {/* Tags List */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Existing Tags ({tags.length})
        </Typography>
        {tags.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No tags yet. Create your first tag to get started.
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: "auto" }}>
            {tags.map((tag) => (
              <ListItem
                key={tag.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => startEdit(tag)}
                      disabled={loading}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => setDeleteConfirmTagId(tag.id)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label={tag.name} size="small" />
                    </Box>
                  }
                  secondary={tag.description || "No description"}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmTagId !== null}
        onClose={() => setDeleteConfirmTagId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this tag?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmTagId(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmTagId && handleDeleteTag(deleteConfirmTagId)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}